import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Seo from "@/components/Seo";
import SolarScene from "@/components/SolarScene";
import RoofStudio from "@/components/RoofStudio";
import ServiceExplorer from "@/components/ServiceExplorer";
import StorageSection from "@/components/StorageSection";
import FinalCta from "@/components/FinalCta";
import Reveal from "@/components/Reveal";
import ProjectsSection from "@/components/ProjectsSection";
import ReviewsSection from "@/components/ReviewsSection";
import ProcessSteps from "@/components/ProcessSteps";
import FourPillarsEngine from "@/components/FourPillars";
import { GALLERY } from "@/lib/gallery";
import { FLORIDA_NAV } from "@/lib/florida-nav";
import IncentivesLicenses from "@/components/IncentivesLicenses";
import AcademyBanner from "@/components/AcademyBanner";
import RoiChat, { ROU_CASH, ROU_DOJO_BANNER, ROU_AVATAR } from "@/components/RoiChat";
import { useSite } from "@/context/SiteContext";
import { HOMEPAGE_FAQ } from "@/lib/services";

const lineVariants = {
  hidden: { y: "112%" },
  show: (i) => ({ y: 0, transition: { delay: 0.12 + i * 0.16, duration: 0.9, ease: [0.16, 1, 0.3, 1] } }),
};

function KineticLine({ i, children }) {
  return (
    <span className="hero-line">
      <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show">
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const { content, openAudit } = useSite();
  const { scrollY } = useScroll();
  const sceneY = useTransform(scrollY, [0, 700], [0, 64]);
  const hero = content.hero;
  const headline = hero.headline || "Turn your commercial electric bill into a smarter investment.";
  const lines = headline.split(/(?<=\.)\s+/).slice(0, 3);

  return (
    <>
      <Seo
        title="Florida Commercial Solar ROI & Bill Review | Coastal Energy"
        description="Commercial solar and storage for Florida businesses: utility-bill reviews, financial modeling and honest ROI math. Upload your commercial electric bill to start — call 310-881-5155."
      />
      <section className="hero">
        <div className="wrap">
          <div className="hero-grid">
            <div className="hero-copy">
              <div className="badge">
                <span className="dot" /> WE INSTALL COMMERCIAL SOLAR.
              </div>
              <h1 data-testid="hero-heading">
                {lines.map((line, i) => (
                  <KineticLine key={i} i={i}>
                    {i === 1 ? <em style={{ fontStyle: "normal", color: "var(--teal)" }}>{line}</em> : line}
                  </KineticLine>
                ))}
              </h1>
              <p data-testid="hero-description">{hero.sub}</p>
              <div style={{ display: "flex", gap: 14, marginTop: 26, flexWrap: "wrap" }}>
                <button className="btn lime" data-testid="hero-upload-cta" onClick={() => openAudit()}>
                  {hero.ctaPrimary || "Upload Your Commercial Electric Bill"} <span>↗</span>
                </button>
                <a className="btn light" href="#roi" data-testid="hero-roi-cta">
                  {hero.ctaSecondary || "Explore Your Solar ROI"} ↓
                </a>
              </div>
              <p className="micro" data-testid="hero-division-line" style={{ marginTop: 10, fontWeight: 700, color: "var(--teal)" }}>
                Coastal Energy's Commercial Solar Division · Commercial sales led by Lawrence Williams.
              </p>
              <p className="micro" style={{ display: "block" }}>
                <span style={{ display: "block", marginBottom: 8 }}>
                  <span className="rou-hero-chip" data-testid="roi-hero-chip">
                    <img src={ROU_AVATAR} alt="Roi Solar, the energy ninja" />
                    <span><b>Roi Solar says:</b> “Your bill is about to get smaller. Stay ready.”</span>
                  </span>
                </span>
                See the jobs we've done:{" "}
                <Link to="/projects" data-testid="hero-jobs-link" style={{ fontWeight: 800, color: "var(--teal)" }}>
                  Our work ↗
                </Link>
              </p>
            </div>
            <div className="hero-art">
              <div className="scene-label">
                <span className="dot" /> INTERACTIVE ENERGY CONCEPT · DRAG TO ROTATE
              </div>
              <motion.div style={{ y: sceneY, height: "100%" }}>
                <SolarScene kind="commercial" state={{ heroSky: true }} fallbackAlt="A commercial rooftop solar array under a bright sunny sky" />
              </motion.div>
              <div className="floating one">
                <span>ONE CONNECTED ENERGY PLAN</span>
                <strong>Solar + storage</strong>
                <span>Built around the way you use power</span>
              </div>
              <div className="floating two">
                <span>✦ THE NEXT STEP</span>
                <strong>Upload your bill.</strong>
                <span className="muted">See the opportunity for your property.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="roi" style={{ padding: "58px 0 10px", background: "var(--mint)", borderTop: "1px solid var(--line)" }}>        <div className="wrap">
          <Reveal>
            <span className="eyebrow">YOUR SOLAR ROI, EXPLAINED</span>
            <h2 style={{ fontSize: "clamp(26px,3vw,40px)", margin: "12px 0 8px" }}>The four pillars of your commercial solar decision.</h2>
            <p className="muted" style={{ fontSize: 13, maxWidth: 720 }}>
              Potential tax benefits · demand-charge reduction · ownership and financing options · payback and long-term returns.
              Everything below is an illustrative scenario from your inputs — verified results come from your property, tariff and tax adviser.
            </p>
          </Reveal>
        </div>
        <FourPillarsEngine variant="embedded" />
      </section>

      <section className="section" id="florida-selector" style={{ paddingTop: 26, paddingBottom: 10 }}>
        <div className="wrap">
          <Reveal>
            <details className="sbb-assumptions" data-testid="florida-region-selector" style={{ background: "white", padding: "18px 22px" }}>
              <summary style={{ fontWeight: 800 }}>Florida? Start with your region's solar economics ▾</summary>
              <p className="sbb-hint" style={{ marginTop: 10 }}>
                Same state, different utility economics. Pick your region for local context — utilities, incentives and the
                bill-based review. Guides open on <Link to="/florida-commercial-solar" style={{ fontWeight: 800 }}>Florida Commercial Solar</Link>.
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                {FLORIDA_NAV.find((g) => g.group === "regions").items.map(([label, href]) => (
                  <Link key={href} className="btn light" to={href} data-testid={`florida-region-${href.split("/").pop()}`}>{label} ↗</Link>
                ))}
              </div>
            </details>
          </Reveal>
        </div>
      </section>

      <IncentivesLicenses />
      <AcademyBanner />

      <section className="roi-band" id="roi-cash" data-testid="roi-cash-band">
        <div className="wrap roi-band-grid">
          <img className="roi-cash-img" src={ROU_CASH} alt="Roi Solar, the energy ninja, sitting on a mountain of savings with his solar sunglasses" loading="lazy" />
          <div className="roi-band-copy">
            <span className="eyebrow">MEET ROI SOLAR · YOUR ENERGY SENSEI</span>
            <h2>
              He sits on hypothetical savings,
              <br />
              so yours stop being hypothetical.
            </h2>
            <p>
              Roi runs the Solar Savings Dojo: solar sizing, potential tax kickbacks, demand-charge reduction, ownership and
              financing, and payback. He keeps it honest — no guaranteed numbers, no tax advice, just a fast track to the people
              who design real projects.
            </p>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <button className="btn" onClick={() => window.dispatchEvent(new CustomEvent("open-roi"))} data-testid="roi-band-chat">
                Chat with Roi <span>↗</span>
              </button>
              <Link className="btn light" to="/projects" data-testid="band-jobs-link">See jobs we've done ↗</Link>
            </div>
          </div>
        </div>
      </section>

      <ProcessSteps />

      <section className="section visualizer" id="visualizer" style={{ paddingTop: 60 }}>
        <div className="wrap">
          <div className="section-heading">
            <div>
              <span className="eyebrow">SEE THE POSSIBILITY</span>
              <h2>
                Meet your building's
                <br />
                solar side.
              </h2>
            </div>
            <p>Start with dimensions. Add a photo. Explore how solar and storage could come together on your property.</p>
          </div>
          <Reveal>
            <RoofStudio />
          </Reveal>
        </div>
      </section>

      <ProjectsSection compact />

      <section className="section" id="field-photos" style={{ paddingTop: 8 }}>
        <div className="wrap">
          <div className="section-heading">
            <div>
              <span className="eyebrow">FROM THE FIELD</span>
              <h2>Real crews. Real roofs. No stock photos.</h2>
            </div>
            <p>A few of the {GALLERY.length} photos in our gallery — rooftop arrays, batteries, inverters and the details in between.</p>
          </div>
          <div className="field-strip" data-testid="home-gallery-strip">
            {[3, 8, 17, 29, 58, 72, 80, 13].map((i) => {
              const img = GALLERY[i];
              return (
                <Link to="/gallery" key={img.src} className="field-item" aria-label={`View photo: ${img.alt}`}>
                  <img src={img.thumb} alt={img.alt} loading="lazy" width={400} height={400} style={{ aspectRatio: `${img.w} / ${img.h}` }} />
                </Link>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 18 }}>
            <Link className="btn" to="/gallery" data-testid="home-gallery-cta">See all {GALLERY.length} photos <span>↗</span></Link>
          </div>
        </div>
      </section>

      <section className="section storage" id="storage">
        <div className="wrap">
          <StorageSection />
        </div>
      </section>

      <section className="section" id="services">
        <div className="wrap">
          <div className="section-heading">
            <div>
              <span className="eyebrow">A PLAN THAT WORKS TOGETHER</span>
              <h2>
                One property.
                <br />
                More possibilities.
              </h2>
            </div>
            <p>Explore each solution, see how it works, and tell us what matters most to you.</p>
          </div>
          <Reveal>
            <ServiceExplorer initialId="commercial" />
          </Reveal>
        </div>
      </section>

      <ReviewsSection />

      <section className="section">
        <div className="wrap faq-grid">
          <Reveal>
            <span className="eyebrow">QUESTIONS, ANSWERED</span>
            <h2>
              A clearer path
              <br />
              starts here.
            </h2>
            <p>No guesswork, no pressure. Learn what changes the design before deciding your next step.</p>
            <a className="btn light" href="tel:+13108815155" style={{ marginTop: 23 }}>
              Talk it through <span>↗</span>
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="homepage-faq">
              {HOMEPAGE_FAQ.map((f) => (
                <details key={f.q}>
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <FinalCta />
      <img
        className="dojo-banner"
        src={ROU_DOJO_BANNER}
        alt="Roi Solar points to the Solar Savings Dojo board: solar sizing, potential tax benefits, demand-charge reduction, ownership and financing, payback and long-term returns"
        data-testid="dojo-banner"
        loading="lazy"
      />
    </>
  );
}
